import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Card, CardHeader, CardTitle, CardContent, Badge, TimeSeriesChart, Button } from '@agion/ui'
import { Search, X } from 'lucide-react'
import { queryRange } from '@/core/observability_api_client'
import type { TimeSeriesPoint } from '@/core/observability_types'

interface ServiceDetailPanelProps {
  serviceName: string
  onClose: () => void
}

function toSeries(label: string, points: TimeSeriesPoint[], color: string) {
  return [{
    label,
    color,
    values: points.map(p => ({ date: new Date(p.time).toISOString(), value: p.value })),
  }]
}

export const ServiceDetailPanel: React.FC<ServiceDetailPanelProps> = ({ serviceName, onClose }) => {
  const { t } = useTranslation('pages')
  const [cpuData, setCpuData] = useState<TimeSeriesPoint[]>([])
  const [memoryData, setMemoryData] = useState<TimeSeriesPoint[]>([])
  const [goroutineData, setGoroutineData] = useState<TimeSeriesPoint[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const now = Math.floor(Date.now() / 1000)
    const start = String(now - 3600)
    const end = String(now)
    const step = '60'

    async function fetchDetail() {
      try {
        const [cpuRes, memRes, goRes] = await Promise.all([
          queryRange(`sum(rate(process_cpu_seconds_total{job="${serviceName}"}[5m])) * 1000`, start, end, step),
          queryRange(`sum(process_resident_memory_bytes{job="${serviceName}"}) / 1024 / 1024`, start, end, step),
          queryRange(`sum(go_goroutines{job="${serviceName}"})`, start, end, step),
        ])

        const toPoints = (values?: [number, string][]): TimeSeriesPoint[] =>
          (values || []).map(([ts, val]) => ({ time: ts * 1000, value: parseFloat(val) || 0 }))

        setCpuData(toPoints(cpuRes.data?.result?.[0]?.values))
        setMemoryData(toPoints(memRes.data?.result?.[0]?.values))
        setGoroutineData(toPoints(goRes.data?.result?.[0]?.values))
      } catch {
        // Charts show empty on failure
      } finally {
        setLoading(false)
      }
    }

    fetchDetail()
  }, [serviceName])

  const ChartSection: React.FC<{
    title: string
    data: TimeSeriesPoint[]
    color: string
    unit: string
  }> = ({ title, data, color, unit }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-40 animate-pulse bg-slate-100 dark:bg-slate-700 rounded" />
        ) : data.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-slate-400">
            {t('observability.noData', 'No data available')}
          </div>
        ) : (
          <TimeSeriesChart
            data={toSeries(title, data, color)}
            height={160}
            showLegend={false}
            showDots={false}
            formatY={(v: number) => `${v.toFixed(1)} ${unit}`}
            formatX={(d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          />
        )}
      </CardContent>
    </Card>
  )

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white dark:bg-slate-800 shadow-xl border-l border-slate-200 dark:border-slate-700 z-50 overflow-y-auto">
      <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{serviceName}</h2>
          <Badge variant="default">{t('observability.lastHour', 'Last hour')}</Badge>
        </div>
        <Button
          variant="ghost"
          onClick={onClose}
          className="p-1.5 h-auto"
        >
          <X className="h-5 w-5 text-slate-500" />
        </Button>
      </div>

      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          <Link to={`/infrastructure/logs?service=${encodeURIComponent(serviceName)}`}>
            <Button variant="outline" className="h-8 text-xs">
              <Search className="h-3.5 w-3.5 mr-1" />
              {t('observability.viewServiceLogs', 'View service logs')}
            </Button>
          </Link>
          <Link to={`/infrastructure/logs?service=${encodeURIComponent(serviceName)}&level=error`}>
            <Button variant="outline" className="h-8 text-xs">
              <Search className="h-3.5 w-3.5 mr-1" />
              {t('observability.viewErrors', 'View errors')}
            </Button>
          </Link>
        </div>
        <ChartSection
          title={t('observability.cpuUsage', 'CPU Usage')}
          data={cpuData}
          color="#3b82f6"
          unit="m"
        />
        <ChartSection
          title={t('observability.memoryUsage', 'Memory Usage')}
          data={memoryData}
          color="#8b5cf6"
          unit="MB"
        />
        <ChartSection
          title={t('observability.goroutines', 'Goroutines')}
          data={goroutineData}
          color="#10b981"
          unit=""
        />
      </div>
    </div>
  )
}
